import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ScanGate,
  cameraAvailable,
  startCamera,
  type CameraCapabilities,
  type CameraInfo,
  type CameraSession,
  type IgnoredKind,
  type ScanKind,
} from '../pos/camera';
import { Icon } from './icons';
import { Alert, Button } from './ui';

export type ScanFlash = 'ok' | 'warn' | 'error';

/** Eine der zuletzt gescannten Positionen („✓ V12 · Hose · 4,00 €“) für die Einblendung im Kamerabild. */
export interface RecentScan {
  id: number;
  kind: ScanFlash;
  text: string;
}

/**
 * Kamerabild zum Scannen von Etiketten (QR und Code 128). Jeder neue Code geht an `onCode`; die Eingabe per Hand oder
 * Handscanner bleibt daneben immer möglich. Ohne Zugriff oder ohne Kamera erscheint ein Hinweis statt eines schwarzen Bildes.
 * `paused` hält die Meldungen an (Warnung, Bezahlen), die Kamera läuft weiter. `flash` färbt den Rahmen kurz (grün, gelb,
 * rot), `recent` blendet die letzten Scans ein, damit man nicht auf die Liste schauen muss. `kind = 'any'` meldet jeden
 * Text statt nur Etikettencodes (Kopplungs-QR). `variant = 'tall'` zeigt einen hohen, quadratischen Sucher mit Zielrahmen
 * (Koppeln), `'strip'` den niedrigen Streifen des Dauerscans. Zoom, Licht und Kamerawechsel erscheinen nur, wo das Gerät sie
 * kann. Mit `?scan-diagnose=1` blendet ein Overlay die Messwerte für die Abnahme am Gerät ein.
 *
 * `toolsPlacement = 'overlay'` (Kasse, U17a): Zoom (ein Knopf, schaltet 1× → 2× → 3× durch), Licht, Kamerawechsel und Ton
 * sind runde Symbolknöpfe im Bild, die Hinweiszeile steht im Bild, unter dem Bild gibt es keine Knöpfe. Ohne `onClose`
 * gibt es keinen Knopf „Kamera schließen“: Die Kamera läuft dann immer.
 */

const HINT_TEXT: Record<IgnoredKind, string> = {
  pairing: 'Das ist ein Kopplungs-Code. Zum Koppeln: Start › Kasse per QR koppeln.',
  runSheet: 'Das ist ein Laufzettel. Er gehört an die Kasse.',
  url: 'Kein Etikett erkannt (QR-Code mit Adresse).',
  other: 'Kein Etikett von BARSO erkannt.',
};
/** Tipp nach 6 s ohne Treffer: beim Koppeln (QR auf einem Bildschirm) anders als bei Etiketten an der Kasse. */
const IDLE_TEXT: Record<ScanKind, string> = {
  any: 'Kein Code erkannt. Tipp: 20–40 cm Abstand, Zoom 2×, Licht, Bildschirm des anderen Geräts heller.',
  label:
    'Kein Code erkannt. Tipp: Etikett 15–30 cm vor die Kamera halten, bei wenig Licht „Licht“.',
  labelOrRunSheet:
    'Kein Code erkannt. Tipp: Etikett 15–30 cm vor die Kamera halten, bei wenig Licht „Licht“.',
};
/** So lange bleibt die Rückmeldung zu fremdem Inhalt stehen. */
const IGNORED_SHOW_MS = 4_000;
const ZOOM_STEPS = [1, 2, 3];

function diagnoseOn(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('scan-diagnose') === '1') {
      sessionStorage.setItem('barso.scan-diagnose', '1');
      return true;
    }
    return sessionStorage.getItem('barso.scan-diagnose') === '1';
  } catch {
    return false;
  }
}

export function CameraScanner({
  onCode,
  onClose,
  paused = false,
  flash = null,
  recent = [],
  kind = 'label',
  variant = 'strip',
  sound,
  toolsPlacement = 'below',
  onAvailability,
  retryable = false,
  runSheetHint,
}: {
  onCode: (code: string) => void;
  /** Ohne Angabe gibt es keinen Knopf „Kamera schließen“ (die Kamera läuft immer). */
  onClose?: () => void;
  paused?: boolean;
  flash?: ScanFlash | null;
  recent?: readonly RecentScan[];
  kind?: ScanKind;
  variant?: 'strip' | 'tall';
  /** Ton-Schalter in der Ecke des Kamerabilds (Kasse, K8); ohne Angabe gibt es keinen. */
  sound?: { on: boolean; onToggle: () => void };
  /** `'overlay'`: Zoom, Licht, Kamerawechsel und Hinweis liegen im Bild; Standard `'below'` (Annahme, Station, Koppeln). */
  toolsPlacement?: 'overlay' | 'below';
  /** Meldet, ob ein Kamerabild läuft (`true`) oder nicht startet (`false`), damit die Seite statt dessen ein Eingabefeld zeigt. */
  onAvailability?: (available: boolean) => void;
  /** Zeigt bei nicht erteilter Freigabe oder Startfehler den Knopf „Kamera erlauben“ (neuer Versuch). */
  retryable?: boolean;
  /** Eigener Hinweis, wenn ein Laufzettel im Bild liegt (Kasse bei ausgeschaltetem Laufzettel); sonst „gehört an die Kasse“. */
  runSheetHint?: string;
}) {
  const runSheetHintRef = useRef(runSheetHint);
  useLayoutEffect(() => {
    runSheetHintRef.current = runSheetHint;
  }, [runSheetHint]);
  const video = useRef<HTMLVideoElement>(null);
  const latest = useRef(onCode);
  const session = useRef<CameraSession | null>(null);
  const pausedNow = useRef(paused);
  // Art des Scans gilt ab dem Start der Kamera (ein Wechsel startet sie nicht neu)
  const scanKind = useRef(kind);
  const [error, setError] = useState<string | null>(null);
  const [caps, setCaps] = useState<CameraCapabilities | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const [torch, setTorch] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [info, setInfo] = useState<CameraInfo | null>(null);
  const [diagnose] = useState(diagnoseOn);
  const [attempt, setAttempt] = useState(0);
  const [canRetry, setCanRetry] = useState(false);
  const availability = useRef(onAvailability);
  // Überlebt das Verdecken der Seite: derselbe Code wird nach dem Neustart nicht doppelt gemeldet
  const [firstGate] = useState(() => new ScanGate(1_500, true, kind));
  const gate = useRef(firstGate);
  const ignoredTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback((started: CameraSession | null): void => {
    setCaps(started?.capabilities ?? null);
    setZoom(started?.zoom ?? null);
    setTorch(started?.torch ?? false);
  }, []);

  // Synchron mit dem Zeichnen: Ein Code, der direkt nach einer Anzeige eintrifft (z. B. Warnung), sieht schon den neuen Zustand
  useLayoutEffect(() => {
    latest.current = onCode;
    availability.current = onAvailability;
  }, [onCode, onAvailability]);

  // Auch eine Kamera, die erst nach dem Pausieren fertig startet, übernimmt den aktuellen Stand
  useLayoutEffect(() => {
    pausedNow.current = paused;
    session.current?.setPaused(paused);
  }, [paused]);

  useEffect(() => {
    const guard = { cancelled: false };
    const isCancelled = (): boolean => guard.cancelled;
    // Ein Start läuft (Freigabe, Kamerawahl dauern): kein zweiter daneben, sonst bliebe eine Kamera offen
    let starting = false;
    // Wann die Kamera zuletzt wegen verdeckter Seite gestoppt wurde (für die Sperre beim Neustart)
    let hiddenAt: number | undefined;
    const wanted = (): boolean => !isCancelled() && !document.hidden;
    const begin = async (element: HTMLVideoElement): Promise<void> => {
      if (!cameraAvailable()) {
        setCanRetry(false);
        setError(
          'Dieses Gerät oder dieser Browser bietet keine Kamera. Bitte tippen Sie den Code ein.',
        );
        availability.current?.(false);
        return;
      }
      if (starting) return;
      starting = true;
      try {
        const started = await startCamera(
          element,
          (code) => {
            setHint(null);
            latest.current(code);
          },
          scanKind.current,
          {
            gate: gate.current,
            ...(hiddenAt === undefined ? {} : { resumeFrom: hiddenAt }),
            onSighting: () => {
              setHint((now) => (now === IDLE_TEXT[scanKind.current] ? null : now));
            },
            onIdle: () => {
              setHint(IDLE_TEXT[scanKind.current]);
            },
            onIgnored: (what) => {
              const text =
                what === 'runSheet' && runSheetHintRef.current !== undefined
                  ? runSheetHintRef.current
                  : HINT_TEXT[what];
              setHint(text);
              if (ignoredTimer.current !== null) clearTimeout(ignoredTimer.current);
              ignoredTimer.current = setTimeout(() => {
                setHint((now) => (now === text ? null : now));
              }, IGNORED_SHOW_MS);
            },
          },
        );
        // Inzwischen geschlossen oder verdeckt: gleich wieder aus (sichtbar startet sie neu)
        if (!wanted()) {
          hiddenAt ??= Date.now();
          started.stop();
        } else {
          session.current = started;
          started.setPaused(pausedNow.current);
          refresh(started);
          availability.current?.(true);
        }
      } catch (caught) {
        if (!wanted()) return; // im Hintergrund abgelehnt: beim Zurückkehren neu versuchen
        const denied = caught instanceof DOMException && caught.name === 'NotAllowedError';
        setCanRetry(true);
        availability.current?.(false);
        setError(
          denied
            ? 'Der Zugriff auf die Kamera ist nicht erlaubt. Bitte erlauben Sie ihn in den Einstellungen des Browsers oder tippen Sie den Code ein.'
            : 'Die Kamera lässt sich nicht starten. Bitte tippen Sie den Code ein.',
        );
      } finally {
        starting = false;
      }
    };
    const element = video.current;
    if (element !== null && !document.hidden) void begin(element);
    // Verdeckte Seite: Kamera stoppen (Akku, Kamera-Anzeige), beim Zurückkehren neu starten
    const onVisibility = (): void => {
      const target = video.current;
      if (target === null) return;
      if (document.hidden) {
        if (session.current !== null) hiddenAt = Date.now();
        session.current?.stop();
        session.current = null;
        refresh(null);
      } else if (session.current === null && !isCancelled()) {
        void begin(target);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      guard.cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (ignoredTimer.current !== null) clearTimeout(ignoredTimer.current);
      session.current?.stop();
      session.current = null;
    };
  }, [refresh, attempt]);

  // Diagnose-Overlay: Messwerte jede Sekunde, nie Codes oder Texte
  useEffect(() => {
    if (!diagnose) return;
    const timer = setInterval(() => {
      setInfo(session.current?.info() ?? null);
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [diagnose]);

  async function changeZoom(value: number): Promise<void> {
    await session.current?.setZoom(value);
    setZoom(session.current?.zoom ?? null);
  }
  async function toggleTorch(): Promise<void> {
    await session.current?.setTorch(!torch);
    setTorch(session.current?.torch ?? false); // nur anzeigen, was das Gerät angenommen hat
  }
  async function switchCamera(): Promise<void> {
    const current = session.current;
    if (current === null) return;
    try {
      await current.switchCamera();
      if (session.current === current) refresh(current);
    } catch {
      // Weder die neue noch die vorige Kamera ließ sich öffnen
      current.stop();
      if (session.current === current) session.current = null;
      setCanRetry(true);
      setError('Die Kamera lässt sich nicht starten. Bitte tippen Sie den Code ein.');
      availability.current?.(false);
    }
  }
  /** „Kamera erlauben“: neuer Startversuch (der Browser fragt erneut oder startet, wenn die Freigabe inzwischen erteilt ist). */
  function retry(): void {
    setError(null);
    setCanRetry(false);
    setAttempt((n) => n + 1);
  }
  /** Zoom im Bild: ein Knopf, der die verfügbaren Stufen der Reihe nach durchschaltet. */
  function cycleZoom(): void {
    const current = zoom ?? 1;
    const next = zoomSteps.find((step) => step > current + 0.05) ?? zoomSteps[0];
    if (next !== undefined) void changeZoom(next);
  }

  const zoomRange = caps?.zoom;
  const zoomSteps =
    zoomRange === undefined
      ? []
      : ZOOM_STEPS.filter((step) => step >= zoomRange.min && step <= zoomRange.max);
  const showTools = zoomSteps.length > 0 || caps?.torch === true || (caps?.cameras ?? 0) > 1;
  const overlay = toolsPlacement === 'overlay';
  const nextZoom = zoomSteps.find((step) => step > (zoom ?? 1) + 0.05) ?? zoomSteps[0];

  return (
    <div className={`camera camera--${variant}${overlay ? ' camera--overlay' : ''}`}>
      {error === null ? (
        <div className={`camera__frame${flash === null ? '' : ` camera__frame--${flash}`}`}>
          <video
            ref={video}
            muted
            playsInline
            aria-label="Kamerabild zum Scannen der Etiketten"
            className="camera__video"
          />
          {variant === 'tall' && <div className="camera__target" aria-hidden="true" />}
          {paused && <div className="camera__paused">Pausiert</div>}
          {sound !== undefined && (
            <button
              type="button"
              className="camera__sound"
              aria-label={sound.on ? 'Ton ausschalten' : 'Ton einschalten'}
              aria-pressed={sound.on}
              title={sound.on ? 'Ton ausschalten' : 'Ton einschalten'}
              onClick={sound.onToggle}
            >
              <Icon name={sound.on ? 'volume' : 'volume-off'} size={22} />
            </button>
          )}
          {diagnose && info !== null && (
            <dl className="camera__diagnose" aria-label="Diagnose">
              <dt>Erkennung</dt>
              <dd>{info.detector === 'detector' ? 'Browser (BarcodeDetector)' : 'ZXing'}</dd>
              <dt>Auflösung</dt>
              <dd>{`${String(info.width)} × ${String(info.height)}`}</dd>
              <dt>Versuche/s</dt>
              <dd>{info.fps}</dd>
              <dt>Kamera</dt>
              <dd>{info.camera ?? 'unbekannt'}</dd>
              <dt>Fokus</dt>
              <dd>{info.focusModes?.join(', ') ?? 'nicht gemeldet'}</dd>
              <dt>Zoom</dt>
              <dd>
                {info.zoomRange === null
                  ? 'nicht möglich'
                  : `${String(info.zoom ?? '–')} (${String(info.zoomRange.min)}–${String(info.zoomRange.max)})`}
              </dd>
              <dt>Licht</dt>
              <dd>{info.torch ? 'möglich' : 'nicht möglich'}</dd>
            </dl>
          )}
          {recent.length > 0 && (
            <ol className="camera__recent" aria-label="Zuletzt gescannt">
              {recent.map((entry) => (
                <li
                  key={entry.id}
                  className={`camera__recent-item camera__recent-item--${entry.kind}`}
                >
                  {entry.text}
                </li>
              ))}
            </ol>
          )}
          {overlay && (
            <p className="camera__hint camera__hint--overlay" role="status" aria-live="polite">
              {hint}
            </p>
          )}
          {overlay && showTools && (
            <div className="camera__overlay" role="group" aria-label="Kamera einstellen">
              {(caps?.cameras ?? 0) > 1 && (
                <button
                  type="button"
                  className="camera__round"
                  aria-label="Kamera wechseln"
                  title="Kamera wechseln"
                  onClick={() => void switchCamera()}
                >
                  <Icon name="camera-switch" size={22} />
                </button>
              )}
              {zoomSteps.length > 1 && (
                <button
                  type="button"
                  className="camera__round camera__round--text"
                  aria-label={`Zoom ${String(zoom === null ? 1 : Math.round(zoom))}-fach, tippen für ${String(nextZoom ?? 1)}-fach`}
                  title="Zoom ändern"
                  onClick={cycleZoom}
                >
                  {`${String(zoom === null ? 1 : Math.round(zoom))}×`}
                </button>
              )}
              {caps?.torch === true && (
                <button
                  type="button"
                  className="camera__round"
                  aria-label="Licht"
                  title="Licht"
                  aria-pressed={torch}
                  onClick={() => void toggleTorch()}
                >
                  <Icon name="flash" size={22} />
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <Alert kind="error">
          <div className="stack">
            <span>{error}</span>
            {retryable && canRetry && (
              <span>
                <Button small variant="secondary" onClick={retry}>
                  Kamera erlauben
                </Button>
              </span>
            )}
          </div>
        </Alert>
      )}
      {variant === 'tall' && error === null && (
        <p className="muted camera__distance">
          Halten Sie das Handy 20–40 cm vor den Code. Bei Bildschirmen die Helligkeit erhöhen.
        </p>
      )}
      {!overlay && (
        <p className="camera__hint" role="status" aria-live="polite">
          {hint}
        </p>
      )}
      {!overlay && showTools && (
        <div className="camera__tools" role="group" aria-label="Kamera einstellen">
          {zoomSteps.map((step) => (
            <Button
              key={step}
              variant="secondary"
              className="camera__tool"
              aria-label={`Zoom ${String(step)}-fach`}
              aria-pressed={zoom !== null && Math.abs(zoom - step) < 0.05}
              onClick={() => void changeZoom(step)}
            >
              {`${String(step)}×`}
            </Button>
          ))}
          {caps?.torch === true && (
            <Button
              variant="secondary"
              className="camera__tool"
              aria-label="Licht"
              aria-pressed={torch}
              onClick={() => void toggleTorch()}
            >
              Licht
            </Button>
          )}
          {(caps?.cameras ?? 0) > 1 && (
            <Button
              variant="secondary"
              className="camera__tool"
              aria-label="Kamera wechseln"
              onClick={() => void switchCamera()}
            >
              Kamera wechseln
            </Button>
          )}
        </div>
      )}
      {onClose !== undefined && (
        <div>
          <Button variant="secondary" small onClick={onClose}>
            Kamera schließen
          </Button>
        </div>
      )}
    </div>
  );
}
